import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Clock, LogIn, LogOut, Shield, Plane, CheckCircle, User } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_345ce5ef-1c7a-4449-8db5-041edeaf083a/artifacts/srp7q3sr_LOGO%20EXSEG.png";
const API_URL = '/api';

const PilotClockInPage = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [clockStatus, setClockStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clocking, setClocking] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await axios.get(`${API_URL}/pilot/clock-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setClockStatus(res.data);
      } catch (error) {
        console.error('Error checking clock status:', error);
      } finally {
        setLoading(false);
      }
    };
    checkStatus();
  }, [token]);

  const handleClockIn = async () => {
    setClocking(true);
    try {
      await axios.post(`${API_URL}/pilot/clock-in`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ponto registrado! Bem-vindo ao servico.');
      setClockStatus({ on_duty: true, message: 'Em servico' });
      setTimeout(() => navigate('/my-project'), 1500);
    } catch (error) {
      toast.error('Erro ao registrar ponto');
    } finally {
      setClocking(false);
    }
  };

  const handleClockOut = async () => {
    setClocking(true);
    try {
      await axios.post(`${API_URL}/pilot/clock-out`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ponto de saida registrado. Ate a proxima!');
      setClockStatus({ on_duty: false, message: 'Fora de servico' });
    } catch (error) {
      toast.error('Erro ao registrar saida');
    } finally {
      setClocking(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-10 h-10 border-3 border-gold/30 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  const onDuty = clockStatus?.on_duty;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="pilot-clock-in-page">
      {/* Top Bar */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Logo" className="h-8 object-contain" />
          <div className="hidden sm:block">
            <p className="text-navy text-xs font-rajdhani font-bold uppercase tracking-wider">Drone Monitor Hub</p>
            <p className="text-gray-400 text-[9px] font-mono">CONTROLE DE PONTO - LGPD</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gold/15 flex items-center justify-center">
              <User size={14} className="text-gold" />
            </div>
            <div className="text-right">
              <p className="text-navy text-xs font-semibold leading-tight">{user?.name || user?.username}</p>
              <p className="text-gray-400 text-[9px] font-mono">PILOTO</p>
            </div>
          </div>
          <button onClick={handleLogout} className="text-gray-400 hover:text-red-500 transition-colors p-2" data-testid="pilot-logout-btn">
            <LogOut size={16} />
          </button>
        </div>
      </header>

      {/* Main Content - full page */}
      <main className="flex-1 flex flex-col p-4 sm:p-6 md:p-8">
        {/* Status Badge */}
        <div className="mb-6">
          {onDuty ? (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-full" data-testid="status-on-duty">
              <span className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse" />
              <span className="text-green-700 text-sm font-semibold">EM SERVICO</span>
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 border border-gray-200 rounded-full" data-testid="status-off-duty">
              <span className="w-2.5 h-2.5 bg-gray-400 rounded-full" />
              <span className="text-gray-600 text-sm font-semibold">FORA DE SERVICO</span>
            </div>
          )}
        </div>

        {/* Clock Display */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 sm:p-8 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock size={18} className="text-gold" />
            <span className="text-gray-500 text-xs font-mono uppercase tracking-wider">Relogio</span>
          </div>
          <p className="text-6xl sm:text-7xl md:text-8xl font-rajdhani font-bold text-navy tabular-nums leading-none" data-testid="clock-display">
            {currentTime.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </p>
          <p className="text-gray-500 text-sm sm:text-base font-mono mt-3 capitalize">
            {currentTime.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
          </p>
        </div>

        {/* Actions - Large touch targets */}
        <div className="space-y-4 flex-1 flex flex-col">
          {!onDuty ? (
            <>
              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <div className="flex items-start gap-3 mb-5">
                  <Shield size={20} className="text-gold mt-0.5" />
                  <div>
                    <p className="text-navy font-semibold text-sm">Registro obrigatorio</p>
                    <p className="text-gray-500 text-xs mt-0.5">Para acessar o sistema, registre seu ponto de entrada conforme LGPD.</p>
                  </div>
                </div>
                <Button
                  onClick={handleClockIn}
                  disabled={clocking}
                  className="w-full h-16 sm:h-20 bg-green-600 hover:bg-green-700 text-white font-rajdhani font-bold uppercase tracking-wider text-lg sm:text-xl rounded-2xl shadow-lg shadow-green-600/20 transition-all hover:shadow-xl active:scale-[0.98]"
                  data-testid="clock-in-btn"
                >
                  {clocking ? (
                    <div className="w-7 h-7 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <><LogIn size={24} className="mr-3" /> BATER PONTO - ENTRADA</>
                  )}
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Access Project - Primary */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6 flex-1 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-xl bg-gold/15 flex items-center justify-center">
                    <Plane size={24} className="text-gold" />
                  </div>
                  <div>
                    <p className="text-navy font-semibold text-base">Pronto para voar</p>
                    <p className="text-gray-500 text-xs">Acesse seu projeto para iniciar as operacoes.</p>
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/my-project')}
                  className="w-full h-16 sm:h-20 bg-navy hover:bg-navy/90 text-white font-rajdhani font-bold uppercase tracking-wider text-lg sm:text-xl rounded-2xl shadow-lg shadow-navy/20 transition-all hover:shadow-xl active:scale-[0.98]"
                  data-testid="access-project-btn"
                >
                  <Plane size={24} className="mr-3" /> ACESSAR MEU PROJETO
                </Button>
              </div>

              {/* Clock Out - Secondary */}
              <div className="bg-white rounded-2xl border border-red-100 p-4">
                <Button
                  onClick={handleClockOut}
                  disabled={clocking}
                  className="w-full h-14 sm:h-16 bg-red-50 border border-red-200 text-red-600 hover:bg-red-100 font-rajdhani font-bold uppercase tracking-wider text-base rounded-xl transition-all active:scale-[0.98]"
                  data-testid="clock-out-btn"
                >
                  {clocking ? (
                    <div className="w-5 h-5 border-2 border-red-300 border-t-red-600 rounded-full animate-spin" />
                  ) : (
                    <><LogOut size={20} className="mr-2" /> REGISTRAR SAIDA</>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-gray-300 text-[10px] font-mono">LGPD - Dados protegidos por politica de seguranca</p>
        </div>
      </main>
    </div>
  );
};

export default PilotClockInPage;
